import Pause from '@/images/Pause.svg';
import Play from '@/images/Play.svg';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import styled from '@emotion/styled';
import { Slider } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import IconButton from '../Buttons/IconButton';

const PlayerContainer = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 24px;
    margin: 0 32px;
    align-items: center;
    height: 100%;
`;

const ProgressContainer = styled.div`
    width: 100%;
    position: relative;
    top: 2px;
`;

const AudioSlider = styled(Slider)`
    color: black;

    & > .MuiSlider-thumb {
        transition-duration: 15ms;
        color: white;

        &:hover {
            box-shadow: none;
        }
    }
    & > .MuiSlider-rail {
        color: ${({ theme }) => theme.colors.secondary.main}45;
    }
    & > .MuiSlider-track {
        transition-duration: 15ms;
        color: ${({ theme }) => theme.colors.secondary.main};
    }
`;

const PositionText = styled.p`
    text-align: right;
    font-size: 16px;
    height: 1rem;
`;

const PlayerIcon = styled.img<{ isPlaying: boolean }>`
    width: 50%;
    height: 50%;
    margin-left: ${props => (props.isPlaying ? 0 : '0.125rem')};
`;

const SliderDurations = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
`;

const playerButtonSx = {
    background: '#1B1C1E',
    alignItems: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    filter: 'drop-shadow(0px 10px 15px rgba(0, 0, 0, 0.1))',
    '&:hover': {
        background: '#1B1C1E',
        opacity: 1,
    },
};

interface Props {
    audioData: AudioData;
    onToggleAudio?: () => void;
    handleAudioError?: (error: Error) => void;
}

function AudioPlayer({ audioData, onToggleAudio, handleAudioError }: Props) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [position, setPosition] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const audioRef = useRef<ReactPlayer>(null);
    const toggleAudio = useCallback(() => {
        onToggleAudio?.();
        setIsPlaying(isPlaying => !isPlaying);
    }, []);

    return (
        <>
            <PlayerContainer>
                <IconButton ariaLabel="play/pause audio" sx={playerButtonSx} onClick={toggleAudio}>
                    <PlayerIcon src={isPlaying ? Pause : Play} isPlaying={isPlaying} />
                </IconButton>
                {audioData && (
                    <ProgressContainer>
                        <AudioSlider
                            min={0}
                            max={audioData?.duration}
                            step={0.001}
                            onChangeCommitted={(_, value) => {
                                audioRef.current?.seekTo(value as number, 'seconds');
                                setPosition(value as number);
                                setIsDragging(false);
                            }}
                            onChange={(_, value) => {
                                setIsDragging(true);
                                setPosition(value as number);
                            }}
                            value={position}
                            aria-label="continuous-slider"
                            color="secondary"
                        />
                        <SliderDurations>
                            <PositionText>
                                {new Date(Math.ceil(audioData.duration - position) * 1000).toISOString().substring(14, 19)}
                            </PositionText>
                            <PositionText>{new Date(Math.ceil(audioData.duration) * 1000).toISOString().substring(14, 19)}</PositionText>
                        </SliderDurations>
                    </ProgressContainer>
                )}
            </PlayerContainer>
            <ReactPlayer
                style={{ display: 'none' }}
                url={audioData.listenUrl}
                ref={audioRef}
                controls={false}
                playing={isPlaying}
                progressInterval={1}
                onProgress={state => {
                    !isDragging && setPosition(Math.ceil(state.playedSeconds * 1000) / 1000);
                }}
                onEnded={() => setIsPlaying(false)}
                onError={handleAudioError}
            />
        </>
    );
}

export default AudioPlayer;
